import React from 'react';
import { Button, Grid, Typography } from '@mui/material';
import { CalendarDay } from 'further-ui/utils';
import { CurrencyCode } from 'further-types/lp/vehicle';
import GridContainer from 'components/GridContainer';
import ButtonGroup from 'components/ButtonGroup';
import EqualisationsTableHeader from 'components/EqualisationElements/EqualisationsTableHeader';
import Spacing from 'components/Spacing';
import FeeCard from './FeeCard';
import { useLayoutContext } from 'contexts/LayoutContext';
import api from 'lib/trpcClient';
import { useFormContext } from 'react-hook-form';
import { PartnerTransfersFormValues } from '../../hooks/usePartnerTransfersForm';
import { PartnerTransferSection } from 'further-types/lp/partner-transfer';

interface FeeAllocation {
  commitmentId: string;
  investorName: string;
  feeExTaxLcy: number;
  salesTaxAmountLcy: number;
  feeIncTaxLcy: number;
  isTransferor: boolean;
}

interface Fee {
  feeKey: string;
  originalFeeId: string;
  lpFeeTemplateFeeName: string;
  accrualDate: CalendarDay;
  approvalDate?: CalendarDay;
  isApproved: boolean;
  originalAllocations: Array<FeeAllocation>;
  newAllocations: Array<FeeAllocation>;
}

interface TransferFeesContentProps {
  partnerTransferId: string;
  fees: Array<Fee>;
  baseCurrency: CurrencyCode;
  transferDate: CalendarDay;
  onNext: () => void;
  onPrevious: () => void;
}

const TransferFeesContent: React.FC<TransferFeesContentProps> = ({
  partnerTransferId,
  fees,
  baseCurrency,
  transferDate,
  onNext,
  onPrevious,
}) => {
  const { selectedVehicleId } = useLayoutContext();
  const { getValues } = useFormContext<PartnerTransfersFormValues>();

  const updatePartnerTransfer = api.partnerTransfers.update.useMutation();

  const handleNext = () => {
    const formValues = getValues();
    const input = {
      partnerTransferId,
      vehicleId: selectedVehicleId ?? '',
      transferDate: formValues.transferDate,
      transferorCommitmentId: formValues.trasferrorCommitmentId,
      currentSection: PartnerTransferSection.Fees,
      transferees: formValues.transferees.map((transferee) => ({
        investorId: transferee.investorId,
        amountLcy: transferee.amountLcy,
        percentage: transferee.percentage,
      })),
      saveActionFees: true,
    };
    updatePartnerTransfer.mutate(input, {
      onSuccess: () => {
        onNext();
      },
    });
  };

  const handlePrevious = () => {
    onPrevious();
  };

  return (
    <GridContainer>
      <Grid size={{ xs: 12, lg: 12 }}>
        {fees.length === 0 ? (
          <Spacing size="lg">
            <Typography variant="body1">
              No approved fees found before the partner transfer date.
            </Typography>
          </Spacing>
        ) : (
          <>
            <EqualisationsTableHeader
              headers={['Name', 'Status', 'Accrual date']}
              gridTemplateColumns="60% 20% 20%"
            />
            {fees.map((fee, index) => (
              <FeeCard
                key={fee.feeKey}
                fee={fee}
                index={index}
                isLastElement={index === fees.length - 1}
                baseCurrency={baseCurrency}
                transferDate={transferDate}
              />
            ))}
          </>
        )}
      </Grid>
      <Grid size={{ xs: 12, lg: 12 }}>
        <ButtonGroup>
          <Button
            variant="outlined"
            onClick={handlePrevious}
            disabled={updatePartnerTransfer.isPending}
          >
            Previous step
          </Button>

          <Button
            variant="contained"
            onClick={handleNext}
            disabled={updatePartnerTransfer.isPending}
            loading={updatePartnerTransfer.isPending}
          >
            Save and continue
          </Button>
        </ButtonGroup>
      </Grid>
    </GridContainer>
  );
};

export default TransferFeesContent;
